import React, { useEffect, useRef, useState, useCallback } from 'react'
import OpenSeadragon from 'openseadragon'
import { Button, ButtonGroup, Spinner } from '@/components/ui-compat'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import {
  faSearchPlus,
  faSearchMinus,
  faExpand,
  faHome,
} from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'

interface Snag {
  id: string
  number: number
  displayNumber: string
  title: string
  status: string
  priority: string
  pinX: number | null
  pinY: number | null
  infrastructureType: string | null
}

interface OpenSeadragonViewerProps {
  dziUrl: string
  dziTilesBaseUrl?: string  // Public S3 URL for tiles (when using S3 direct access)
  dziWidth: number
  dziHeight: number
  snags: Snag[]
  selectedSnagId: string | null
  isPlacementMode: boolean
  onMarkerClick: (snagId: string) => void
  onPlaceMarker: (position: { x: number; y: number }) => void
  isFullscreen?: boolean
  focusPin?: { x: number; y: number } | null
}

// Fraction of the drawing width shown around the pin when focusing (Task 4b).
const FOCUS_WIDTH_RATIO = 0.4

const clamp = (value: number, min: number, max: number) =>
  max < min ? (min + max) / 2 : Math.min(Math.max(value, min), max)

export interface CustomTileSourceOptions {
  height: number
  width: number
  tileSize: number
  tileOverlap: number
  minLevel: number
  maxLevel: number
  getTileUrl: (level: number, x: number, y: number) => string
}

/**
 * Parse DZI XML and create a custom tile source for OpenSeadragon
 * This allows using presigned URL for the DZI descriptor while serving tiles from a public URL
 */
export async function createCustomTileSource(
  dziUrl: string,
  tilesBaseUrl: string
): Promise<CustomTileSourceOptions> {
  // Fetch and parse the DZI XML
  const response = await fetch(dziUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch DZI: ${response.status}`)
  }

  const dziXml = await response.text()
  const parser = new DOMParser()
  const doc = parser.parseFromString(dziXml, 'application/xml')

  // Extract DZI parameters
  const imageElement = doc.querySelector('Image')
  const sizeElement = doc.querySelector('Size')

  if (!imageElement || !sizeElement) {
    throw new Error('Invalid DZI format')
  }

  const tileSize = parseInt(imageElement.getAttribute('TileSize') || '256', 10)
  const overlap = parseInt(imageElement.getAttribute('Overlap') || '1', 10)
  const format = imageElement.getAttribute('Format') || 'jpeg'
  const width = parseInt(sizeElement.getAttribute('Width') || '0', 10)
  const height = parseInt(sizeElement.getAttribute('Height') || '0', 10)

  // Create custom tile source options
  return {
    width,
    height,
    tileSize,
    tileOverlap: overlap,
    minLevel: 0,
    maxLevel: Math.ceil(Math.log2(Math.max(width, height))),
    getTileUrl: (level: number, x: number, y: number): string => {
      return `${tilesBaseUrl}/${level}/${x}_${y}.${format}`
    },
  }
}

export const STATUS_COLORS: Record<string, string> = {
  OPEN: '#dc3545',
  REOPENED: '#dc3545',
  IN_PROGRESS: '#ffc107',
  RESOLVED: '#28a745',
  VERIFIED: '#17a2b8',
  CLOSED: '#6c757d',
}

// Calculate SVG marker size based on number of digits
function getMarkerSizeForNumber(num: number | null | undefined): { width: number; height: number; fontSize: number } {
  if (num == null) return { width: 24, height: 30, fontSize: 8 }
  const digits = num.toString().length
  if (digits <= 1) return { width: 24, height: 30, fontSize: 8 }
  if (digits === 2) return { width: 28, height: 35, fontSize: 7 }
  if (digits === 3) return { width: 34, height: 42, fontSize: 6 }
  if (digits === 4) return { width: 42, height: 52, fontSize: 5 }
  return { width: 50, height: 62, fontSize: 4 } // 5+ digits
}

// Priority adds extra size on top of digit-based sizing
const PRIORITY_BONUS: Record<string, number> = {
  LOW: 0,
  MEDIUM: 4,
  HIGH: 8,
  CRITICAL: 12,
}

const OpenSeadragonViewer: React.FC<OpenSeadragonViewerProps> = ({
  dziUrl,
  dziTilesBaseUrl,
  dziWidth,
  dziHeight,
  snags,
  selectedSnagId,
  isPlacementMode,
  onMarkerClick,
  onPlaceMarker,
  isFullscreen = false,
  focusPin,
}) => {
  const { t } = useTranslation()
  const viewerRef = useRef<HTMLDivElement>(null)
  const osdRef = useRef<OpenSeadragon.Viewer | null>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // The backend re-signs dziUrl on every response, so the same drawing arrives as
  // a different string each time the query hits the network. Key the viewer on the
  // S3 object path instead — that identifies the drawing itself — and read the
  // current signed URL from a ref, so a rotated signature no longer tears down and
  // rebuilds OpenSeadragon (which also dropped every marker and lost the pin focus).
  const dziObjectKey = dziUrl?.split('?')[0]
  const dziUrlRef = useRef(dziUrl)
  dziUrlRef.current = dziUrl

  // Initialize OpenSeadragon viewer
  useEffect(() => {
    if (!viewerRef.current) return

    let viewer: OpenSeadragon.Viewer | null = null
    let cancelled = false

    // A re-init destroys the previous viewer and nulls osdRef, so readiness must
    // drop with it: isReady is what tells the focus effect and the marker overlay
    // that a live viewer exists, and setIsReady(true) on an already-true state is
    // a no-op that leaves both holding a dead ref forever.
    setIsReady(false)
    setIsLoading(true)

    const initViewer = async () => {
      try {
        setLoadError(null)

        // Determine tile source - use custom source for S3 direct access
        // When dziTilesBaseUrl is provided, we fetch DZI via presigned URL
        // and load tiles from public S3 URL
        let tileSources: string | CustomTileSourceOptions = dziUrlRef.current
        if (dziTilesBaseUrl) {
          tileSources = await createCustomTileSource(dziUrlRef.current, dziTilesBaseUrl)
        }

        if (cancelled) return

        viewer = OpenSeadragon({
          element: viewerRef.current!,
          // @ts-ignore - OpenSeadragon supports custom tile sources with getTileUrl
          tileSources,
          prefixUrl: 'https://cdn.jsdelivr.net/npm/openseadragon@5.0/build/openseadragon/images/',
          showNavigationControl: false,
          animationTime: 0.3,
          blendTime: 0.1,
          constrainDuringPan: true,
          minZoomLevel: 0.5,
          maxZoomLevel: 10,
          visibilityRatio: 0.5,
          springStiffness: 15,
          gestureSettingsMouse: {
            clickToZoom: false,
            dblClickToZoom: true,
            flickEnabled: true,
          },
          gestureSettingsTouch: {
            flickEnabled: true,
            pinchRotate: false,
          } as OpenSeadragon.GestureSettings,
        })

        osdRef.current = viewer

        viewer.addHandler('open', () => {
          if (!cancelled) {
            setIsReady(true)
            setIsLoading(false)
          }
        })

        viewer.addHandler('open-failed', () => {
          if (!cancelled) {
            setIsLoading(false)
            setLoadError(t('Snags_dzi_load_failed'))
            console.error('Failed to load DZI tiles')
          }
        })
      } catch (error) {
        if (!cancelled) {
          console.error('Error initializing viewer:', error)
          setLoadError(error instanceof Error ? error.message : t('Snags_viewer_init_failed'))
          setIsLoading(false)
        }
      }
    }

    initViewer()

    return () => {
      cancelled = true
      if (viewer) {
        viewer.destroy()
      }
      osdRef.current = null
    }
  }, [dziObjectKey, dziTilesBaseUrl])

  // Handle canvas click for placement mode using OpenSeadragon's event system
  useEffect(() => {
    if (!osdRef.current || !isReady) return

    const viewer = osdRef.current

    const handleCanvasClick = (event: OpenSeadragon.CanvasClickEvent) => {
      if (!isPlacementMode || !event.quick) {
        // event.quick is false if it was a drag
        return
      }

      // Convert to viewport coordinates
      const viewportPoint = viewer.viewport.pointFromPixel(event.position)

      // Convert to percentage (0-100)
      const x = viewportPoint.x * 100
      const y = (viewportPoint.y * (dziWidth / dziHeight)) * 100

      if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
        onPlaceMarker({ x, y })
      }
    }

    viewer.addHandler('canvas-click', handleCanvasClick)

    return () => {
      viewer.removeHandler('canvas-click', handleCanvasClick)
    }
  }, [isReady, isPlacementMode, dziWidth, dziHeight, onPlaceMarker])

  // Fit the viewport to a rect centered on focusPin (percent 0-100), clamped inside
  // the drawing (same math as SnagPinPreview's fitToPin, but a wider crop). Re-runs
  // whenever the viewer becomes ready or the focusPin object identity changes.
  useEffect(() => {
    if (!osdRef.current || !isReady || !focusPin) return

    const viewer = osdRef.current
    const el = viewerRef.current
    if (!el || el.clientWidth === 0) return

    const maxY = dziHeight / dziWidth
    const vx = focusPin.x / 100
    const vy = (focusPin.y / 100) * maxY

    const rectW = FOCUS_WIDTH_RATIO
    const rectH = rectW * (el.clientHeight / el.clientWidth)
    const cx = clamp(vx, rectW / 2, 1 - rectW / 2)
    const cy = clamp(vy, rectH / 2, maxY - rectH / 2)

    viewer.viewport.fitBounds(
      new OpenSeadragon.Rect(cx - rectW / 2, cy - rectH / 2, rectW, rectH),
      false
    )
  }, [isReady, focusPin, dziWidth, dziHeight])

  // Zoom controls
  const handleZoomIn = () => {
    if (osdRef.current) {
      osdRef.current.viewport.zoomBy(1.5)
      osdRef.current.viewport.applyConstraints()
    }
  }

  const handleZoomOut = () => {
    if (osdRef.current) {
      osdRef.current.viewport.zoomBy(0.67)
      osdRef.current.viewport.applyConstraints()
    }
  }

  const handleResetZoom = () => {
    if (osdRef.current) {
      osdRef.current.viewport.goHome()
    }
  }

  // Filter snags that have valid pin coordinates
  const pinnedSnags = snags.filter((snag) => snag.pinX !== null && snag.pinY !== null)

  // Convert snag position to viewport position for overlay
  const getMarkerStyle = (snag: Snag): React.CSSProperties | null => {
    if (snag.pinX === null || snag.pinY === null || !osdRef.current || !isReady) {
      return null
    }

    const viewer = osdRef.current

    // Convert percentage to viewport coordinates
    const viewportX = snag.pinX / 100
    const viewportY = (snag.pinY / 100) * (dziHeight / dziWidth)

    // Convert to pixel position
    const pixelPoint = viewer.viewport.pixelFromPoint(
      new OpenSeadragon.Point(viewportX, viewportY)
    )

    return {
      position: 'absolute' as const,
      left: pixelPoint.x,
      top: pixelPoint.y,
      transform: 'translate(-50%, -100%)',
      pointerEvents: 'auto' as const,
    }
  }

  // Update overlay positions on viewport change
  useEffect(() => {
    if (!osdRef.current || !isReady) return

    const viewer = osdRef.current
    const updateOverlay = () => {
      if (overlayRef.current) {
        // Force re-render by updating a counter
        overlayRef.current.style.transform = `translateZ(0)`
      }
    }

    viewer.addHandler('animation', updateOverlay)
    viewer.addHandler('animation-finish', updateOverlay)

    return () => {
      viewer.removeHandler('animation', updateOverlay)
      viewer.removeHandler('animation-finish', updateOverlay)
    }
  }, [isReady])

  return (
    <div className="relative w-full" style={isFullscreen ? { flex: 1 } : { height: '65vh' }}>
      {/* Loading indicator */}
      {isLoading && (
        <div
          className="absolute flex flex-col items-center justify-center"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            backgroundColor: '#f8f9fa',
          }}
        >
          <Spinner color="primary" className="mb-3" />
          <small className="text-muted-foreground">{t('Snags_loading_blueprint', 'Loading blueprint...')}</small>
        </div>
      )}

      {/* Error indicator */}
      {loadError && !isLoading && (
        <div
          className="absolute flex flex-col items-center justify-center"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            backgroundColor: '#f8f9fa',
          }}
        >
          <div className="text-destructive mb-2">
            <i className="fas fa-exclamation-triangle fa-2x" />
          </div>
          <small className="text-destructive">{loadError}</small>
        </div>
      )}

      {/* Placement mode banner */}
      {isPlacementMode && (
        <div
          className="absolute w-full text-center py-2"
          style={{
            top: 0,
            left: 0,
            zIndex: 99,
            background: 'linear-gradient(180deg, rgba(255,193,7,0.95) 0%, rgba(255,193,7,0.8) 100%)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <span className="font-bold text-foreground">
            {t('Snags_click_to_place', 'Click on the blueprint to place marker')}
          </span>
        </div>
      )}

      {/* Zoom controls */}
      {isReady && (
        <div
          style={{
            position: 'absolute',
            top: isPlacementMode ? 50 : 10,
            right: 10,
            zIndex: 50,
          }}
        >
          <ButtonGroup size="sm" className="shadow-sm">
            <Button
              color="light"
              onClick={handleZoomIn}
              title={t('Snags_zoom_in', 'Zoom in')}
              className="border"
            >
              <Icon icon={faSearchPlus as any} />
            </Button>
            <Button
              color="light"
              onClick={handleZoomOut}
              title={t('Snags_zoom_out', 'Zoom out')}
              className="border"
            >
              <Icon icon={faSearchMinus as any} />
            </Button>
            <Button
              color="light"
              onClick={handleResetZoom}
              title={t('Snags_reset_zoom', 'Reset')}
              className="border"
            >
              <Icon icon={faExpand as any} />
            </Button>
          </ButtonGroup>
        </div>
      )}

      {/* OpenSeadragon container */}
      <div
        ref={viewerRef}
        style={{
          width: '100%',
          height: '100%',
          cursor: isPlacementMode ? 'crosshair' : 'grab',
          backgroundColor: '#f8f9fa',
        }}
      />

      {/* Markers overlay */}
      {isReady && (
        <MarkersOverlay
          ref={overlayRef}
          viewer={osdRef.current}
          snags={pinnedSnags}
          selectedSnagId={selectedSnagId}
          onMarkerClick={onMarkerClick}
          dziWidth={dziWidth}
          dziHeight={dziHeight}
        />
      )}
    </div>
  )
}

// Separate component for markers to handle updates
interface MarkersOverlayProps {
  viewer: OpenSeadragon.Viewer | null
  snags: Snag[]
  selectedSnagId: string | null
  onMarkerClick: (snagId: string) => void
  dziWidth: number
  dziHeight: number
}

const MarkersOverlay = React.forwardRef<HTMLDivElement, MarkersOverlayProps>(
  ({ viewer, snags, selectedSnagId, onMarkerClick, dziWidth, dziHeight }, ref) => {
    const [, forceUpdate] = useState(0)

    useEffect(() => {
      if (!viewer) return

      const update = () => forceUpdate((n) => n + 1)

      viewer.addHandler('animation', update)
      viewer.addHandler('animation-finish', update)
      viewer.addHandler('zoom', update)
      viewer.addHandler('pan', update)

      return () => {
        viewer.removeHandler('animation', update)
        viewer.removeHandler('animation-finish', update)
        viewer.removeHandler('zoom', update)
        viewer.removeHandler('pan', update)
      }
    }, [viewer])

    if (!viewer) return null

    return (
      <div
        ref={ref}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        {snags.map((snag) => {
          if (snag.pinX === null || snag.pinY === null) return null

          // Convert percentage to viewport coordinates
          const viewportX = snag.pinX / 100
          const viewportY = (snag.pinY / 100) * (dziHeight / dziWidth)

          // Convert to pixel position
          const pixelPoint = viewer.viewport.pixelFromPoint(
            new OpenSeadragon.Point(viewportX, viewportY)
          )

          const isSelected = selectedSnagId === snag.id
          const color = STATUS_COLORS[snag.status] || '#6c757d'
          const { width: digitWidth, height: digitHeight, fontSize } = getMarkerSizeForNumber(snag.number)
          const priorityBonus = PRIORITY_BONUS[snag.priority] || 0
          const baseWidth = digitWidth + priorityBonus
          const baseHeight = digitHeight + Math.round(priorityBonus * 1.25) // Maintain aspect ratio
          const markerWidth = isSelected ? baseWidth + 8 : baseWidth
          const markerHeight = isSelected ? baseHeight + 10 : baseHeight

          return (
            <div
              key={snag.id}
              onClick={(e) => {
                e.stopPropagation()
                onMarkerClick(snag.id)
              }}
              style={{
                position: 'absolute',
                left: pixelPoint.x,
                top: pixelPoint.y,
                transform: 'translate(-50%, -100%)',
                pointerEvents: 'auto',
                cursor: 'pointer',
                zIndex: isSelected ? 100 : 10,
              }}
            >
              {/* Marker pin */}
              <div
                style={{
                  width: markerWidth,
                  height: markerHeight,
                  position: 'relative',
                  transition: 'all 0.2s ease',
                }}
              >
                <svg
                  viewBox="0 0 24 30"
                  style={{
                    width: '100%',
                    height: '100%',
                    filter: isSelected
                      ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                      : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                  }}
                >
                  <path
                    d="M12 0C5.4 0 0 5.4 0 12c0 9 12 18 12 18s12-9 12-18c0-6.6-5.4-12-12-12z"
                    fill={color}
                  />
                  <circle cx="12" cy="12" r="6" fill="white" />
                  <text
                    x="12"
                    y="15"
                    textAnchor="middle"
                    fontSize={fontSize}
                    fontWeight="bold"
                    fill={color}
                  >
                    {snag.number}
                  </text>
                </svg>
              </div>
            </div>
          )
        })}
      </div>
    )
  }
)

MarkersOverlay.displayName = 'MarkersOverlay'

export default OpenSeadragonViewer
